<template>
  <nav
    ref="navContainer"
    class="core-table-of-contents"
  >
    <div
      ref="wrapper"
      class="core-table-of-contents__block-wrapper"
    >
      <span class="core-table-of-contents__title"> {{ title }} </span>
      <div
        ref="desktopScrollbar"
        class="custom-scrollbar"
      >
        <ol class="list">
          <li
            v-for="(heading, idx) of headings"
            :key="`desktop-nav-heading-${idx}`"
            :class="{ active: activeIndex === idx && !isMobileView }"
            :style="`--start: &quot;${idx + 1}&quot;`"
          >
            <a
              class="core-table-of-contents__link"
              :href="heading.link"
              @click="(e) => handleClick(e, idx)"
              >{{ heading.content }}</a
            >
          </li>
        </ol>
      </div>
    </div>

    <div
      v-show="!isInView && isMobileView"
      :class="{ 'sticky-nav--opened': isMenuOpened }"
      class="sticky-nav"
    >
      <div
        class="sticky-nav__title-wrapper"
        @click="toggleMenu"
      >
        <span class="core-table-of-contents__title sticky-nav__title">
          {{ title }}
        </span>
        <SvgAsset
          class="sticky-nav__icon"
          :content="arrowPath"
        />
      </div>

      <div
        ref="mobileScrollbar"
        class="custom-scrollbar"
      >
        <ol class="list">
          <li
            v-for="(heading, idx) of headings"
            :key="`mobile-nav-heading-${idx}`"
            :class="{ active: activeIndex === idx }"
            :style="`--start: &quot;${idx + 1}&quot;`"
          >
            <a
              class="core-table-of-contents__link"
              :href="heading.link"
              @click="(e) => handleClick(e, idx)"
              >{{ heading.content }}</a
            >
          </li>
        </ol>
      </div>
    </div>
    <div
      v-if="isMenuOpened"
      class="dark-background"
      @click="toggleMenu"
    />
  </nav>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { debounce, throttle } from 'throttle-debounce';
  import type { TContent } from '~/types/common';
  import { BREAKPOINTS } from '~/constants';
  import { useWindowStore } from '~/store/window';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import arrowPath from '@/assets/images/svg/arrow.svg?raw';
  import { lockScroll, unlockScroll } from '@devhacker/shared/utils/dom/scrollLock.ts';

  const STICKY_HEADER_OFFSET = 50;
  // В мобильном дизайне отступ включает в себя полоску оглавление
  const MOBILE_HEADING_OFFSET = STICKY_HEADER_OFFSET * 2;
  // Offset to include paddings
  const LIST_ELEM_OFFSET = 10;
  // Widgets wrapper class
  const WIDGETS_CLASS = 'widgets-renderer';

  type Heading = {
    content: string;
    link: string;
    level: number;
  };

  export default defineNuxtComponent({
    name: 'CoreTableOfContents',

    components: {
      SvgAsset,
    },

    props: {
      elem: {
        type: Object as PropType<TContent>,
        required: true,
      },
    },

    data: () => {
      return {
        isInView: true,
        isMenuOpened: false,
        activeIndex: -1,
        title: 'Оглавление',
        scrolling: true,
        scrollEndTimeout: null as NodeJS.Timeout | null,
      };
    },

    computed: {
      arrowPath: () => arrowPath,
      windowWidth() {
        return useWindowStore().windowWidth;
      },

      isMobileView(): boolean {
        return this.windowWidth <= BREAKPOINTS.BIG_TABLET;
      },

      headings(): Heading[] {
        return this.elem.attributes.headings;
      },

      headersNodes(): NodeListOf<HTMLHeadingElement> {
        return document.querySelectorAll('main h2[id^="id"]');
      },

      articleElem(): Element | null {
        return document.getElementById('article-content');
      },

      beforeHeaderParagraphElements(): Element[] {
        // precalculate previous elements of headers
        return Array.from(this.headersNodes).map((header: Element) => {
          let prev = header.previousElementSibling;
          while (prev?.previousElementSibling && prev.className === WIDGETS_CLASS) {
            prev = prev.previousElementSibling;
          }
          return prev || header;
        });
      },
    },

    watch: {
      async isMenuOpened(isOpen: boolean): Promise<void> {
        if (isOpen) {
          lockScroll();
          await this.$nextTick();
          this.scrollToMenuItem();
        } else {
          unlockScroll();
        }
      },
    },

    async mounted() {
      if (this.isMobileView) {
        this.setIsNavPartiallyInViewPort();
        document.addEventListener('scroll', this.throttledScrollForNav(), {
          passive: true,
        });
      }

      document.addEventListener('scroll', this.throttledScrollForMenu(), {
        passive: true,
      });

      if ('scrollend' in window) {
        document.addEventListener('scrollend', this.debouncedSetEnd());
      } else {
        // setup custom scrollend event for safari
        document.addEventListener('scroll', this.setUpScrollEnd);
      }

      // await tick for partial content load
      await this.$nextTick();

      // Default anchor scroll behavior not working untill page fully loaded
      // Page loads for long time cause of async scripts, so trigger scroll programmaticaly
      this.$route.hash && document.getElementById(this.$route.hash.slice(1))?.scrollIntoView(true);
    },

    beforeUnmount() {
      document.removeEventListener('scroll', this.throttledScrollForMenu());
      document.removeEventListener('scroll', this.throttledScrollForNav());

      if ('scrollend' in window) {
        document.removeEventListener('scrollend', this.debouncedSetEnd());
      } else {
        document.removeEventListener('scroll', this.setUpScrollEnd);
      }
    },

    methods: {
      setUpScrollEnd(): void {
        this.scrollEndTimeout && clearTimeout(this.scrollEndTimeout);
        this.scrollEndTimeout = setTimeout(() => {
          this.setScrollend();
        }, 100);
      },

      debouncedSetEnd(): () => void {
        // on initial loading a lot of scroll events fired
        // combine all of them until anchor scroll ends
        // otherwise scroll interrupted by scrollTo menu item
        return debounce(700, false, this.setScrollend);
      },

      setScrollend(): void {
        this.scrolling = false;
        this.scrollToMenuItem();
      },

      throttledScrollForMenu(): () => void {
        return throttle(200, this.checkMenuItem);
      },

      throttledScrollForNav(): () => void {
        return throttle(200, this.setIsNavPartiallyInViewPort);
      },

      handleClick(e: MouseEvent, clickedIndex: number): void {
        /* Грузим много ассинхронных скриптов, до полной загрузки не работает скролл Якоря и т.д.
         потому до момента пока страница полностью не загрузится симулируем работу якорей */
        if (document.readyState !== 'complete') {
          // Не работает js скролл без prevent собыытия якоря
          e.preventDefault();
          // Скролл вне основного потока, тк на мобилке он полностью заблокирован
          setTimeout(() => {
            this.headersNodes[clickedIndex].scrollIntoView({
              block: 'start',
              behavior: 'smooth',
            });
          }, 0);
          // Обновляем id в url без тригера события, location.hash тригерит изменение url
          history.replaceState({}, '', `#id${clickedIndex + 1}`);
        }

        this.scrolling = true;
        this.isMenuOpened = false;
        this.sendAnalytics(this.headings[clickedIndex]);
      },

      sendAnalytics(heading: Heading) {
        this.$sendYandexMetrika({
          level1: 'Оглавление_выбор пункта',
          level4: heading.link,
          level5: heading.content,
        });
      },

      checkMenuItem(): void {
        if (!this.headersNodes) {
          return;
        }

        // Check nodes only if scrolled past first header and didn't sroll past whole article
        //  otherwise reset to no selected index
        if (
          this.headersNodes[0].getBoundingClientRect().bottom >= window.innerHeight ||
          (this.articleElem?.getBoundingClientRect().bottom || 0) < 0
        ) {
          this.activeIndex = -1;
          return;
        }

        let closestIndex = 0;
        const headerHeight = this.isMobileView ? MOBILE_HEADING_OFFSET : STICKY_HEADER_OFFSET;

        // Check previous sibling of header, after it's not in view make the header active
        this.beforeHeaderParagraphElements.forEach((elem: Element, i: number) => {
          const { bottom: prevElemBottom = 0 } = elem.getBoundingClientRect() || {};

          // We check previous sibling, so every element that can be usefull is scrolled past
          if (prevElemBottom > headerHeight) {
            return;
          }

          closestIndex = i;
        });

        this.activeIndex = closestIndex;
        !this.isMobileView && this.scrollToMenuItem();
      },

      scrollToMenuItem(): void {
        const container = (
          this.isMobileView ? this.$refs.mobileScrollbar : this.$refs.desktopScrollbar
        ) as HTMLDivElement;

        if (this.activeIndex < 0 || !container || this.scrolling) {
          return;
        }

        const activeHeaderLink = this.headings[this.activeIndex].link;
        const element = container.querySelector<HTMLElement>(`a[href*="${activeHeaderLink}"]`);

        if (!element) {
          return;
        }

        // Element position inside scrollbar
        const itemPos = element.getBoundingClientRect().top - container.getBoundingClientRect().top;
        // Element height
        const itemHeight = element.offsetHeight;

        // Check if element outside of scrollbar view, eg less than 0 or more then scrollbar height
        if (itemPos > 0 && itemPos + itemHeight < container.offsetHeight) {
          return;
        }

        // if elem lower than view scroll to elem position + offset + already scrolled offset
        // if elem upper that view, scroll to elem position - offset
        const scrollOffset =
          itemPos > 0
            ? itemPos + itemHeight - container.offsetHeight + container.scrollTop + LIST_ELEM_OFFSET
            : container.scrollTop + itemPos - LIST_ELEM_OFFSET;

        // scroll into view does not include margin/padding so we have to calc it manually
        container.scrollTo({
          top: scrollOffset,
          behavior: this.isMobileView ? 'auto' : 'smooth',
        });
      },

      setIsNavPartiallyInViewPort(): void {
        if (!this.$refs.navContainer || !this.isMobileView) {
          return;
        }

        const rect = (this.$refs.wrapper as HTMLElement).getBoundingClientRect();

        // Slight offset of menu for ux
        this.isInView = rect.bottom >= STICKY_HEADER_OFFSET + 40;
      },

      toggleMenu(): void {
        this.isMenuOpened = !this.isMenuOpened;

        const actionName = this.isMenuOpened ? 'Развернуть' : 'Свернуть';

        this.$sendYandexMetrika({
          level1: 'Оглавление_клик',
          level4: 'Без перехода',
          level5: actionName,
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use '@/scss/amp/bh-list.amp.scss' as *;
  @use '@devhacker/shared/styles/scrollbar.scss' as scrollbar;

  .custom-scrollbar {
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;

    @include scrollbar.customScrollbar;
  }

  ol {
    margin-bottom: 0;
    width: 100%;

    li {
      display: flex;
      padding: 8px 0px 0px;
      margin-bottom: 0px !important;

      @include tablet {
        margin-top: 0px;
      }

      @include big-tablet {
        padding-bottom: 8px;
        padding: 8px 24px;
      }

      &.active {
        background: #e4fcfb;
      }

      a.core-table-of-contents__link {
        @include link-styles;
        border-bottom: none;

        &:visited {
          color: $link-color--1;
        }
      }
    }
  }

  .core-table-of-contents {
    // Нужен из-за хака с отступами заголовков
    // Margin заголовка перекрывает последний пункт меню на мобилке
    z-index: 1;
    padding: 0px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 32px 0px;

    :deep() ~ h2 {
      scroll-margin-top: 100px;
      scroll-snap-margin-top: 100px;

      @include big-tablet {
        scroll-margin-top: 50px;
        scroll-snap-margin-top: 50px;
      }
    }

    @include tablet {
      margin: 40px 0px;
    }

    @include big-tablet {
      padding: 16px 0px;
      margin: 0px;
    }

    &__title {
      @include headline-small-text;
      font-family: $headline-font--1;

      font-weight: 400;
      padding: 8px 0px 4px;

      @include tablet {
        @include headline-medium-text;
      }

      @include big-tablet {
        @include headline-small-text;
        font-family: $text-font--1;
        font-weight: 500;
        padding: 8px 24px 4px;
      }
    }

    &__block-wrapper {
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    ol li:before {
      min-width: 28px;
      margin-right: 8px;
    }
  }

  .sticky-nav {
    @include mediumText;

    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    z-index: 12;
    top: 47px;
    max-height: 48px;
    left: 0px;
    background: #ffffff;
    position: fixed;
    width: 100%;
    overflow: hidden;
    overscroll-behavior: contain;
    will-change: max-height;

    &__icon {
      :deep() {
        svg path {
          stroke: #c722ca;
        }
      }
      transition: transform 0.3s;
      transform: rotate(90deg);
      width: 24px;
      height: 24px;
    }

    ol {
      margin: 16px 0px;

      li {
        padding: 8px 24px 8px;

        &:before {
          min-width: 24px;
        }

        @include normalText;

        @include tablet {
          @include mediumText;
        }
      }
    }

    &__title {
      @include mediumText;
      font-family: $text-font--1;
      line-height: 26px;
      padding: 0;
      color: white;

      &-wrapper {
        display: flex;
        justify-content: center;
        width: 100%;
        gap: 8px;
        padding: 11px 0px;
        align-items: center;
        background: #000000;
      }
    }

    &--opened {
      max-height: calc(100dvh - 130px);

      .sticky-nav__icon {
        transform: rotate(270deg);
      }
    }
  }

  .dark-background {
    display: block;
    background: rgba(0, 0, 0, 0.5);
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 11;
  }
</style>
